import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { qrPrintApi } from '../../api/qrPrintApi';
import QrProduct3DViewer from '../../components/qrPrint/3d/QrProduct3DViewer';
import { useAuth } from '../../contexts/AuthContext';
import {
  getOptionValue,
  getUserEstablishmentId,
} from '../../utils/qrPrintHelpers';

import '../../styles/QrPrint/QrPrintAiCustomizer.css';

const DEFAULT_CONFIGURATION = {
  restaurant_logo: true,
  restaurant_name: '',
  custom_text: 'Scan to view our menu',
  background_color: '#ffffff',
  text_color: '#1f2937',
  accent_color: '#51766e',
  qr: {
    enabled: true,
    locked: true,
    size: 140,
    foreground_color: '#111111',
    background_color: '#ffffff',
    margin: 4,
    rotation: 0,
    opacity: 1,
  },
  three_d: {
    enabled: false,
    surface_finish: 'glossy',
    base_style: 'classic',
  },
};

const getResponseData = (response) => response?.data?.data;

const getCollectionItems = (response) => {
  const data = getResponseData(response);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const sameId = (left, right) =>
  String(left || '') === String(right || '');

const getMenuName = (menu) =>
  menu?.name || menu?.title || menu?.translations?.[0]?.name || '';

const cloneValue = (value) => {
  if (value === undefined) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(value));
};

const getSelectedOptionsForConfiguration = ({
  configuration,
  selectedSize,
  selectedMaterial,
  selectedColor,
  previewMode,
}) => ({
  size: selectedSize || null,
  material: selectedMaterial || null,
  color:
    configuration?.selected_product_color ||
    configuration?.accent_color ||
    selectedColor ||
    null,
  preview_mode: previewMode || null,
});

const buildEditorElementsFromConfiguration = ({
  product,
  configuration,
  selectedOptions,
}) => {
  const blocks = product?.editor_schema?.blocks || [];
  const existing = configuration?.editor_elements || {};
  const elements = {};

  blocks.forEach((block) => {
    if (!block?.id || block.enabled === false) {
      return;
    }

    const value =
      existing[block.id] !== undefined
        ? cloneValue(existing[block.id])
        : cloneValue(block.default ?? {});

    if (block.type === 'text') {
      const nextValue = {
        ...(typeof value === 'object' ? value : {}),
      };

      const normalizedId = String(block.id).toLowerCase();
      const normalizedLabel = String(block.label || '').toLowerCase();

      if (
        normalizedId.includes('restaurant') ||
        normalizedLabel.includes('restaurant')
      ) {
        nextValue.text =
          configuration?.restaurant_name ||
          nextValue.text ||
          '';
      } else if (
        normalizedId.includes('call') ||
        normalizedId.includes('action') ||
        normalizedLabel.includes('call') ||
        normalizedLabel.includes('action')
      ) {
        nextValue.text =
          configuration?.custom_text ||
          nextValue.text ||
          'Scan to view our menu';
      }

      if (!nextValue.color) {
        nextValue.color =
          configuration?.text_color ||
          '#1f2937';
      }

      elements[block.id] = nextValue;
      return;
    }

    if (block.type === 'qr') {
      const qrSize = Number(configuration?.qr?.size || 140);

      elements[block.id] = {
        ...(typeof value === 'object' ? value : {}),
        size:
          Number.isFinite(qrSize)
            ? Math.max(18, Math.min(60, Math.round((qrSize / 316) * 100)))
            : value?.size || 34,
        foreground_color:
          configuration?.qr?.foreground_color ||
          value?.foreground_color ||
          '#111111',
        background_color:
          configuration?.qr?.background_color ||
          value?.background_color ||
          '#ffffff',
      };
      return;
    }

    if (block.type === 'background_color') {
      elements[block.id] =
        configuration?.background_color ||
        value ||
        '#ffffff';
      return;
    }

    if (block.type === 'product_color') {
      elements[block.id] =
        selectedOptions.color ||
        value ||
        configuration?.accent_color ||
        '';
      return;
    }

    if (block.type === 'material') {
      elements[block.id] =
        selectedOptions.material ||
        value ||
        '';
      return;
    }

    if (block.type === 'surface_finish') {
      elements[block.id] =
        configuration?.three_d?.surface_finish ||
        value ||
        'glossy';
      return;
    }

    if (block.type === 'three_d_property') {
      const property = block.settings?.property;

      elements[block.id] =
        property && configuration?.three_d?.[property] !== undefined
          ? configuration.three_d[property]
          : value;
      return;
    }

    elements[block.id] = value;
  });

  return {
    ...existing,
    ...elements,
  };
};

const buildAcceptedConfiguration = ({
  product,
  configuration,
  selectedSize,
  selectedMaterial,
  selectedColor,
}) => {
  const previewMode =
    configuration?.preview_mode ||
    (configuration?.three_d?.enabled ? '3d' : null) ||
    (product?.preview_mode === '2d' ? '2d' : '3d');

  const selectedOptions = getSelectedOptionsForConfiguration({
    configuration,
    selectedSize,
    selectedMaterial,
    selectedColor,
    previewMode,
  });

  const acceptedConfiguration = {
    ...DEFAULT_CONFIGURATION,
    ...(configuration || {}),
    accepted_from_ai: true,
    preview_mode: previewMode,
    selected_options: selectedOptions,
    selected_product_size: selectedOptions.size,
    selected_product_material: selectedOptions.material,
    selected_product_color: selectedOptions.color,
    accent_color:
      configuration?.accent_color ||
      selectedOptions.color ||
      DEFAULT_CONFIGURATION.accent_color,
    qr: {
      ...DEFAULT_CONFIGURATION.qr,
      ...(configuration?.qr || {}),
      enabled: true,
      locked: true,
      rotation: 0,
      opacity: 1,
    },
    three_d: {
      ...DEFAULT_CONFIGURATION.three_d,
      ...(configuration?.three_d || {}),
      enabled: previewMode === '3d',
    },
  };

  acceptedConfiguration.editor_elements =
    buildEditorElementsFromConfiguration({
      product,
      configuration: acceptedConfiguration,
      selectedOptions,
    });

  return acceptedConfiguration;
};

export default function QrPrintAiCustomizer() {
  const { user } = useAuth();
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const menuId =
    params.get('menuId') ||
    localStorage.getItem('selected_menu_id') ||
    '';

  const establishmentId =
    params.get('establishmentId') ||
    localStorage.getItem('selected_menu_establishment_id') ||
    localStorage.getItem('selected_establishment_id') ||
    getUserEstablishmentId(user) ||
    '';

  const [product, setProduct] = useState(null);
  const [menu, setMenu] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [configuration, setConfiguration] =
    useState(DEFAULT_CONFIGURATION);
  const [customization, setCustomization] = useState(null);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  const [request, setRequest] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  const loadProductSafely = async () => {
    try {
      const response = await qrPrintApi.getProduct(productId);
      const directProduct = getResponseData(response);

      if (directProduct) {
        return directProduct;
      }
    } catch (directError) {
      console.warn(
        'Direct QR product load failed, trying product list fallback:',
        directError,
      );
    }

    const productsResponse = await qrPrintApi.getProducts({
      per_page: 100,
    });

    return getCollectionItems(productsResponse).find((item) =>
      sameId(item?.id, productId),
    ) || null;
  };

  const loadMenuSafely = async () => {
    let menuFromShow = null;

    if (menuId) {
      try {
        const response = await qrPrintApi.getMenu(menuId);
        const data = getResponseData(response);

        menuFromShow = data?.menu ?? data;

        if (menuFromShow) {
          return menuFromShow;
        }
      } catch (directError) {
        console.warn(
          'Direct menu load failed, trying establishment menus fallback:',
          directError,
        );
      }
    }

    if (!establishmentId) {
      return null;
    }

    const menusResponse = await qrPrintApi.getMenus(establishmentId, {
      per_page: 100,
    });

    const menus = getCollectionItems(menusResponse);

    return (
      menus.find((item) => sameId(item?.id, menuId)) ||
      menus.find((item) => item?.is_default || item?.is_active) ||
      menus[0] ||
      null
    );
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!establishmentId) {
        setError(
          'An establishment is required before AI customization.',
        );
        setLoading(false);
        return;
      }

      try {
        const [nextProduct, nextMenu] =
          await Promise.all([
            loadProductSafely(),
            loadMenuSafely(),
          ]);

        if (!active) return;

        if (!nextProduct) {
          setError(
            'The selected QR print product could not be loaded. Check qrPrintApi.js and the /qr-print/products route.',
          );
          return;
        }

        if (!nextMenu) {
          setError(
            'No menu could be loaded for this establishment. Select a menu again before opening AI customization.',
          );
          return;
        }

        let nextAnalysis = null;

        try {
          const analysisResponse =
            await qrPrintApi.analyzeAiProduct(nextProduct.id, {
              menu_id: Number(nextMenu.id),
            });

          nextAnalysis = getResponseData(analysisResponse) || null;
        } catch (analysisError) {
          console.warn(
            'AI analysis failed, continuing with fallback menu data:',
            analysisError,
          );
        }

        if (!nextAnalysis) {
          nextAnalysis = {
            business: {
              name:
                nextMenu?.establishment?.name ||
                nextMenu?.business?.name ||
                'Detected',
            },
            menu: {
              name: getMenuName(nextMenu) || 'Selected menu',
              theme: 'Custom',
              background_color: '#ffffff',
              text_color: '#1f2937',
              primary_color: '#b45309',
              accent_color: '#b45309',
            },
          };
        }

        if (!active) return;

        setProduct(nextProduct);
        setMenu(nextMenu);
        setAnalysis(nextAnalysis);

        setSelectedSize(
          getOptionValue(nextProduct?.available_sizes?.[0]) || '',
        );

        setSelectedMaterial(
          getOptionValue(
            nextProduct?.available_materials?.[0],
          ) || '',
        );

        setSelectedColor(
          getOptionValue(nextProduct?.available_colors?.[0]) || '',
        );

        const menuStyle = nextAnalysis?.menu || {};

        setConfiguration((current) => ({
          ...current,
          restaurant_name:
            nextMenu?.establishment?.name ||
            nextAnalysis?.business?.name ||
            '',
          background_color:
            menuStyle.background_color ||
            current.background_color,
          text_color:
            menuStyle.text_color ||
            current.text_color,
          accent_color:
            menuStyle.accent_color ||
            menuStyle.primary_color ||
            current.accent_color,
          qr: {
            ...current.qr,
          },
          three_d: {
            ...current.three_d,
            enabled:
              nextProduct?.preview_mode === '3d' ||
              nextProduct?.preview_mode === 'both',
          },
        }));

        setError('');
      } catch (requestError) {
        if (!active) return;

        console.error('QR print AI customizer load failed:', requestError);

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            'The selected product or menu could not be loaded.',
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [
    establishmentId,
    menuId,
    productId,
  ]);

  const menuUrl = menu?.slug
    ? `${window.location.origin}/menu/${menu.slug}`
    : window.location.origin;

  const previewMode =
    product?.preview_mode === '2d'
      ? '2d'
      : '3d';

  const generate = async () => {
    if (!product || !menu) return;

    setGenerating(true);
    setError('');

    try {
      const response =
        await qrPrintApi.generateAiCustomization(
          product.id,
          {
            establishment_id: Number(establishmentId),
            menu_id: Number(menu.id),
            configuration,
            selected_options: {
              size: selectedSize || null,
              material: selectedMaterial || null,
              color: selectedColor || null,
            },
            user_request:
              request.trim() || null,
            previous_customization_id:
              customization?.id || null,
          },
        );

      const result = response.data?.data;

      const nextConfiguration =
        result?.configuration || configuration;

      setCustomization(result);
      setConfiguration(
        buildAcceptedConfiguration({
          product,
          configuration: nextConfiguration,
          selectedSize,
          selectedMaterial,
          selectedColor,
        }),
      );
      setRequest('');
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          'AI customization failed. Try again or use a shorter request.',
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (
      !loading &&
      !error &&
      product?.id &&
      menu?.id &&
      !customization
    ) {
      generate();
    }
    // Only trigger initial generation once after analysis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const accept = async () => {
    if (!customization?.id) return;

    setAccepting(true);
    setError('');

    try {
      await qrPrintApi.acceptAiCustomization(
        customization.id,
      );

      const acceptedConfiguration =
        buildAcceptedConfiguration({
          product,
          configuration,
          selectedSize,
          selectedMaterial,
          selectedColor,
        });

      const query = new URLSearchParams({
        menuId: String(menu.id),
        establishmentId: String(establishmentId),
      });

      navigate(
        `/qr-print/products/${product.id}/customize?${query.toString()}`,
        {
          state: {
            aiConfiguration: acceptedConfiguration,
            aiSelectedOptions:
              acceptedConfiguration.selected_options,
            aiPreviewMode:
              acceptedConfiguration.preview_mode,
            aiCustomizationId: customization.id,
            aiStyleName: customization.style_name,
          },
        },
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          'The AI customization could not be accepted.',
      );
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="qr-print-ai-page-loading">
        <Loader2 size={28} />
        <strong>Analyzing business and menu...</strong>
      </div>
    );
  }

  return (
    <main className="qr-print-ai-customizer-page">
      <header className="qr-print-ai-customizer-hero">
        <div>
          <Link
            className="qr-print-ai-customizer-back"
            to={`/qr-print/products/${productId}/customize?menuId=${menuId}&establishmentId=${establishmentId}`}
          >
            <ArrowLeft size={17} />
            Back to customization
          </Link>

          <span className="qr-print-ai-customizer-eyebrow">
            <Sparkles size={15} />
            AI product designer
          </span>

          <h1>
            AI styling for {product?.name || 'selected product'}
          </h1>

          <p>
            We analyzed this business, its menu style and colors,
            then styled only the product you selected.
          </p>
        </div>
      </header>

      {error ? (
        <div className="qr-print-ai-customizer-error">
          {error}
        </div>
      ) : null}

      <section className="qr-print-ai-analysis-strip">
        <div>
          <span>Business</span>
          <strong>
            {analysis?.business?.name || 'Detected'}
          </strong>
        </div>

        <div>
          <span>Menu</span>
          <strong>{analysis?.menu?.name || menu?.name}</strong>
        </div>

        <div>
          <span>Menu theme</span>
          <strong>
            {analysis?.menu?.theme || 'Custom'}
          </strong>
        </div>

        <div>
          <span>Primary color</span>
          <strong className="qr-print-ai-color-value">
            <i
              style={{
                background:
                  analysis?.menu?.primary_color ||
                  analysis?.menu?.accent_color ||
                  configuration.accent_color,
              }}
            />
            {analysis?.menu?.primary_color ||
              analysis?.menu?.accent_color ||
              configuration.accent_color}
          </strong>
        </div>
      </section>

      <div className="qr-print-ai-customizer-layout">
        <section className="qr-print-ai-result-panel">
          <div className="qr-print-ai-result-heading">
            <div>
              <span>AI result</span>
              <h2>
                {customization?.style_name ||
                  'Generating your style'}
              </h2>

              <p>
                {customization?.style_reason ||
                  'The design is being adapted to your business.'}
              </p>
            </div>

            {customization ? (
              <span className="qr-print-ai-revision-badge">
                Revision {customization.revision}
              </span>
            ) : null}
          </div>

          <div className="qr-print-ai-result-preview">
            {previewMode === '3d' ? (
              <QrProduct3DViewer
                product={product}
                menuUrl={menuUrl}
                configuration={configuration}
                selectedMaterial={selectedMaterial}
                selectedSize={selectedSize}
                productColor={configuration.accent_color}
                restaurantName={
                  configuration.restaurant_name ||
                  analysis?.business?.name ||
                  'Restaurant'
                }
              />
            ) : (
              <div
                className="qr-print-ai-flat-preview"
                style={{
                  backgroundColor:
                    configuration.background_color,
                  color: configuration.text_color,
                  '--ai-accent':
                    configuration.accent_color,
                }}
              >
                <span className="qr-print-ai-flat-accent" />

                <strong>
                  {configuration.restaurant_name ||
                    analysis?.business?.name ||
                    'Restaurant'}
                </strong>

                <div className="qr-print-ai-flat-qr">
                  <QRCodeSVG
                    value={menuUrl}
                    size={configuration.qr?.size || 140}
                    level="H"
                    marginSize={configuration.qr?.margin || 4}
                    bgColor={
                      configuration.qr?.background_color ||
                      '#ffffff'
                    }
                    fgColor={
                      configuration.qr?.foreground_color ||
                      '#111111'
                    }
                  />
                </div>

                <p>{configuration.custom_text}</p>
              </div>
            )}
          </div>
        </section>

        <aside className="qr-print-ai-request-panel">
          <div>
            <span className="qr-print-ai-request-kicker">
              <RefreshCw size={15} />
              Want changes?
            </span>

            <h2>Tell AI what to adjust</h2>

            <p>
              Add a text request and AI will revise this same
              selected product. It will not switch products.
            </p>
          </div>

          <textarea
            rows="6"
            value={request}
            placeholder="Example: make it more luxurious, use warmer tones and shorter text..."
            onChange={(event) =>
              setRequest(event.target.value)
            }
          />

          <button
            type="button"
            className="qr-print-ai-revise-button"
            disabled={generating}
            onClick={generate}
          >
            {generating ? (
              <Loader2 size={18} className="is-spinning" />
            ) : (
              <Sparkles size={18} />
            )}

            {customization
              ? 'Generate revision'
              : 'Generate design'}
          </button>

          <div className="qr-print-ai-request-divider" />

          <button
            type="button"
            className="qr-print-ai-accept-button"
            disabled={
              !customization ||
              generating ||
              accepting
            }
            onClick={accept}
          >
            {accepting ? (
              <Loader2 size={18} className="is-spinning" />
            ) : (
              <CheckCircle2 size={18} />
            )}

            Accept this design
          </button>
        </aside>
      </div>
    </main>
  );
}